import { TotalResultAmounts } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/types';

type AmountsValue = {
    initialValue: number;
    previsionalValue: number;
    valueToCompare: number;
    realizedAmount: number;
    previousYearRealizedAmount: number;
};

/**
 * Calculate the amounts to display in each key indicator based on the total result amounts, the current date and the year indicators.
 *
 * @param {TotalResultAmounts} totalAmounts - the total result amounts
 * @param {boolean} isStartYear - indicate if the year displayed is the start year
 * @param {boolean} isPastYear - indicate if the year displayed is in the past (< currentYear)
 * @return {AmountsValue} the calculated amounts to display in the key indicators.
 */
export const getAmounts = (
    totalAmounts: TotalResultAmounts,
    isStartYear: boolean,
): AmountsValue => {
    const {
        previousYearRealizedTotalAmount,
        currentYearRealizedTotalAmountUntilStartDate,
        currentYearSimulatedTotalAmountFromStartDate,
        currentYearRealizedTotalAmountUntilLastMonth,
        currentYearSimulatedTotalAmountFromLastMonth,
        nextYearSimulatedTotalAmount,
        nextYearRealizedTotalAmount,
        nextYearSimulatedTotalAmountFromLastMonth,
    } = totalAmounts;
    // The initial value contains all simulated amounts from the start of the start date simulation
    // the missing dates are filled with realized data from the start of the year until the before the start year
    const initialValue = isStartYear
        ? currentYearRealizedTotalAmountUntilStartDate +
          currentYearSimulatedTotalAmountFromStartDate
        : nextYearSimulatedTotalAmount;
    // The previsionalValue returns all realized amounts for the current year
    // the missing dates after today are filled with simulated amounts
    const previsionalValue = isStartYear
        ? currentYearRealizedTotalAmountUntilLastMonth +
          currentYearSimulatedTotalAmountFromLastMonth
        : nextYearRealizedTotalAmount +
          nextYearSimulatedTotalAmountFromLastMonth;
    // The valueToCompare value returns the previous year realized amounts of the current date displayed
    // the missing values are filled with simulated amounts
    const valueToCompare = isStartYear
        ? previousYearRealizedTotalAmount
        : currentYearRealizedTotalAmountUntilStartDate +
          currentYearSimulatedTotalAmountFromStartDate;
    // The realized amount contains all realized amounts
    const realizedAmount = isStartYear
        ? currentYearRealizedTotalAmountUntilLastMonth
        : nextYearRealizedTotalAmount;
    const previousYearRealizedAmount = isStartYear
        ? previousYearRealizedTotalAmount
        : currentYearRealizedTotalAmountUntilLastMonth +
          currentYearSimulatedTotalAmountFromLastMonth;
    return {
        initialValue,
        previsionalValue,
        valueToCompare,
        previousYearRealizedAmount,
        realizedAmount,
    };
};
